import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  fetchMeetingEventsInRange,
  fetchZoomDiscoveredMeetings,
  autoImportCalendarMeetings,
} from "../services/meetings.service";

dayjs.extend(utc);
dayjs.extend(timezone);

const BROWSER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

/** A meeting already in Prospo, or a calendar event that isn't yet. */
export type CalendarEvent = {
  id: string;
  title: string;
  start: Date;
  end: Date;
  /** Drives the colour: "general" renders grey, anything else renders white/Advice. */
  category: string;
  /** False for calendar events with no Prospo record — these get the "+ Add" affordance. */
  inProspo: boolean;
  meetingMedium: string;
  joinUrl: string | null;
  /** Present only when inProspo — the Prospo meeting this event came from. */
  meeting?: any;
  /** Present only when !inProspo — the payload POST /meetings/outlook/import expects. */
  raw?: any;
  /**
   * How the event was classified. "oneToOneUnknown" is the yellow row: a 1-1
   * whose counterparty isn't a Prospo client yet, so "+ Add" runs the wizard
   * rather than the plain client picker.
   */
  shape?: "oneToOneKnown" | "oneToOneUnknown" | "group";
  /** The person to create a contact + client for, on an oneToOneUnknown row. */
  counterparty?: { name: string; email: string };
};

/**
 * Same rule as the backend's buildExternalId (services/meetingDiscovery/eventMatching.ts).
 * Must stay byte-identical or already-imported events reappear as "not in Prospo".
 */
const buildExternalId = (iCalUId: string | undefined, utcStartIso: string, graphEventId?: string) =>
  iCalUId ? `${iCalUId}|${utcStartIso}` : `outlook-event|${graphEventId || utcStartIso}`;

const isZoomUrl = (url?: string | null) => !!url && /zoom\.us\//i.test(url);

const extractJoinUrl = (ev: any): string | null =>
  ev?.onlineMeeting?.joinUrl || ev?.onlineMeetingUrl || null;

const resolveMedium = (ev: any): string => {
  const joinUrl = extractJoinUrl(ev);
  if (isZoomUrl(joinUrl)) return "Zoom";
  if (ev?.onlineMeetingProvider === "teamsForBusiness" || ev?.isOnlineMeeting) return "Teams Meeting";
  return "Outlook Calendar";
};

/**
 * Every identifier a Prospo meeting might be recognised by on the calendar.
 *
 * externalId/graphEventId/iCalUId come flat off GET /meetings (MeetingCoreController's
 * getMeetings) — that endpoint picks individual fields rather than returning
 * MeetingMediumMetaData wholesale, so read the flat shape, not a nested one.
 */
const prospoMeetingKeys = (m: any): string[] => {
  const keys: string[] = [];
  if (m?.externalId) keys.push(String(m.externalId));
  if (m?.graphEventId) keys.push(String(m.graphEventId));

  if (m?.iCalUId && m?.utcStartTime) {
    keys.push(buildExternalId(m.iCalUId, dayjs(m.utcStartTime).toISOString()));
  }

  // Zoom and Calendly meetings arrive by webhook rather than import, so they carry
  // no Graph id — the join URL is the only thing shared with the calendar event.
  if (m?.meetingLink) keys.push(String(m.meetingLink).split("?")[0]);

  return keys;
};

const meetingStart = (m: any): Dayjs =>
  m?.utcStartTime
    ? dayjs(m.utcStartTime)
    : dayjs.tz(`${m?.start?.date}T${m?.start?.time}`, m?.timeZone || "Asia/Kolkata").local();

const meetingEnd = (m: any): Dayjs =>
  m?.utcEndTime
    ? dayjs(m.utcEndTime)
    : dayjs.tz(`${m?.end?.date}T${m?.end?.time}`, m?.timeZone || "Asia/Kolkata").local();

/**
 * Everything on the advisor's calendar for the visible range: Prospo meetings
 * plus any calendar event that has no Prospo record yet.
 *
 * Calendar events are additive — a fetch failure (Outlook not connected, Graph
 * down) leaves the Prospo meetings rendering normally rather than emptying the
 * calendar, same principle as useDiscoveredMeetings.
 */
export function useCalendarEvents(
  prospoMeetings: any[],
  rangeStart: Dayjs,
  rangeEnd: Dayjs,
  enabled: boolean,
  /** Called after an auto-import run creates meetings, so the caller can refetch. */
  onAutoImported?: (imported: Array<{ title: string; clientName: string }>) => void,
) {
  const [externalEvents, setExternalEvents] = useState<any[]>([]);
  const [zoomEvents, setZoomEvents] = useState<any[]>([]);
  const [shapes, setShapes] = useState<Record<string, { shape: string; counterparty?: any }>>({});
  // externalIds the server has auto-imported. They stay hidden until the parent's
  // refetch supplies the real Prospo meeting — otherwise the calendar shows the
  // stale event and the new meeting side by side for a beat.
  const [autoImported, setAutoImported] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  // Kept in a ref so a changing callback identity doesn't retrigger the fetch.
  const onAutoImportedRef = useRef(onAutoImported);
  onAutoImportedRef.current = onAutoImported;

  // Refetching on every Dayjs identity change would loop — the objects are new
  // each render even when the day hasn't moved.
  const startKey = rangeStart.startOf("day").toISOString();
  const endKey = rangeEnd.endOf("day").toISOString();
  const requestRef = useRef(0);

  // Declared before `load` so a refresh can clear it: the set is what stops the
  // auto-import effect re-sending the same event, and a refresh is precisely the
  // moment that memory must be dropped.
  const attemptedRef = useRef<Set<string>>(new Set());

  const load = useCallback(async (opts?: { reclassify?: boolean }) => {
    if (!enabled) return;
    const requestId = ++requestRef.current;
    setLoading(true);

    // Classification is server-side and depends on which clients exist, so a
    // cached shape is only valid while that set is unchanged. Creating a client
    // is exactly what invalidates it, so an explicit refresh drops the shapes
    // and the "already attempted" memory, letting the effect below re-classify
    // from scratch.
    //
    // Without this, creating a client left every one of that person's meetings
    // still flagged `oneToOneUnknown` — the banner kept counting them until a
    // full page reload rebuilt the hook.
    //
    // Scoped to an explicit refresh on purpose: `load` also runs whenever the
    // visible range changes, and clearing shapes there would drop valid
    // classifications for events still on screen and re-POST them on every
    // calendar navigation.
    if (opts?.reclassify) {
      attemptedRef.current = new Set();
      setShapes({});
    }

    // Independent settlement: an advisor with Outlook but no Zoom (or the
    // reverse) must still see the source they do have. Only a calendar failure
    // raises `failed` — Zoom being absent is the norm, not an error worth
    // showing.
    const [calendar, zoom] = await Promise.allSettled([
      fetchMeetingEventsInRange(BROWSER_TZ, startKey, endKey),
      fetchZoomDiscoveredMeetings(startKey, endKey),
    ]);

    // A slower earlier request must not overwrite a newer one's results.
    if (requestId !== requestRef.current) return;

    if (calendar.status === "fulfilled") {
      setExternalEvents((calendar.value as any)?.data || []);
      setFailed(false);
    } else {
      setExternalEvents([]);
      setFailed(true);
    }

    const zoomList = zoom.status === "fulfilled" ? ((zoom.value as any)?.events || []) : [];
    setZoomEvents(zoomList);
    setLoading(false);
  }, [enabled, startKey, endKey]);

  useEffect(() => {
    // Bare call: a range change keeps existing classifications (see `load`).
    load();
  }, [load]);

  const events = useMemo<CalendarEvent[]>(() => {
    const prospoEvents: CalendarEvent[] = (prospoMeetings || []).map((m: any) => {
      // A Calendly booking from someone we don't know arrives as a real Prospo
      // meeting with no client attached — the webhook deliberately doesn't
      // invent one. Flag it like an unknown 1-1 so it renders yellow and offers
      // the same create-contact/create-client wizard.
      const unassigned = !m.clientid;
      const invitee = m?.MeetingMediumMetaData?.event;

      return {
        id: String(m._id),
        title: m.title || "(No title)",
        start: meetingStart(m).toDate(),
        end: meetingEnd(m).toDate(),
        category: m.category || "financial",
        inProspo: true,
        meetingMedium: m.meetingMedium || "",
        joinUrl: m.meetingLink || null,
        meeting: m,
        ...(unassigned && invitee?.email
          ? {
              shape: "oneToOneUnknown" as const,
              counterparty: {
                name: invitee.name || invitee.email,
                email: invitee.email,
              },
            }
          : {}),
      };
    });

    const known = new Set(
      (prospoMeetings || []).flatMap(prospoMeetingKeys).filter(Boolean),
    );

    const external: CalendarEvent[] = (externalEvents || [])
      .filter((ev: any) => ev?.start?.dateTime && ev?.end?.dateTime && !ev?.isCancelled)
      // A seriesMaster is the recurrence definition, not a real occurrence — Graph
      // returns the occurrences separately and both would otherwise render.
      .filter((ev: any) => ev?.type !== "seriesMaster")
      .map((ev: any) => {
        const startIso = dayjs(ev.start.dateTime).toISOString();
        const joinUrl = extractJoinUrl(ev);
        return {
          ev,
          externalId: buildExternalId(ev.iCalUId, startIso, ev.id),
          joinUrl,
          startIso,
        };
      })
      .filter(({ ev, externalId, joinUrl }) => {
        if (known.has(externalId)) return false;
        if (ev.id && known.has(String(ev.id))) return false;
        if (joinUrl && known.has(String(joinUrl).split("?")[0])) return false;
        return true;
      })
      .map(({ ev, externalId, joinUrl, startIso }) => ({
        id: externalId,
        title: ev.subject || "(No subject)",
        start: dayjs(ev.start.dateTime).toDate(),
        end: dayjs(ev.end.dateTime).toDate(),
        // Not in Prospo means no category has been chosen yet; it is set when added.
        category: "",
        inProspo: false,
        meetingMedium: resolveMedium(ev),
        joinUrl,
        // Shaped to match what POST /meetings/outlook/import reads off `event`.
        raw: {
          graphEventId: ev.id,
          iCalUId: ev.iCalUId || null,
          externalId,
          title: ev.subject || "(No subject)",
          bodyPreview: ev.bodyPreview || "",
          utcStartTime: startIso,
          utcEndTime: dayjs(ev.end.dateTime).toISOString(),
          isPast: dayjs(ev.end.dateTime).isBefore(dayjs()),
          isTeams: ev.onlineMeetingProvider === "teamsForBusiness" || !!ev.isOnlineMeeting,
          isZoom: isZoomUrl(joinUrl),
          joinUrl,
          attendees: [
            ...(ev.organizer?.emailAddress?.address
              ? [{
                  email: ev.organizer.emailAddress.address,
                  name: ev.organizer.emailAddress.name || ev.organizer.emailAddress.address,
                  isInternal: true,
                  suggestedInclude: false,
                }]
              : []),
            ...(ev.attendees || [])
              .filter((a: any) => a?.emailAddress?.address)
              .map((a: any) => ({
                email: a.emailAddress.address,
                name: a.emailAddress.name || a.emailAddress.address,
                isInternal: false,
                suggestedInclude: true,
              })),
          ],
        },
      }));

    // ── Zoom-only meetings ────────────────────────────────────────────────
    // Already DiscoveredEvent-shaped and deduped against Prospo server-side, so
    // the only remaining overlap is a Zoom meeting that ALSO sits on the Outlook
    // calendar — same meeting, two sources. The join URL is the shared key.
    const externalJoinUrls = new Set(
      external.map((e) => e.joinUrl).filter(Boolean).map((u) => String(u).split("?")[0]),
    );

    const zoomOnly: CalendarEvent[] = (zoomEvents || [])
      .filter((z: any) => {
        const join = z?.joinUrl ? String(z.joinUrl).split("?")[0] : "";
        if (join && externalJoinUrls.has(join)) return false;
        return !known.has(String(z?.externalId));
      })
      .map((z: any) => ({
        id: `zoom-${z.externalId}`,
        title: z.title || "Zoom Meeting",
        start: dayjs(z.utcStartTime).toDate(),
        end: dayjs(z.utcEndTime).toDate(),
        category: "",
        inProspo: false,
        meetingMedium: "Zoom",
        joinUrl: z.joinUrl || null,
        // The backend already returns exactly what the import endpoint expects.
        raw: z,
      }));

    // Overlay the server's classification, and drop anything it just imported —
    // that event is a Prospo meeting now and returns via prospoMeetings.
    const withShape = [...external, ...zoomOnly]
      .filter((e) => !autoImported.has(String(e.raw?.externalId ?? e.id)))
      .map((e) => {
        const key = String(e.raw?.externalId ?? e.id);
        const info = shapes[key];
        return info
          ? { ...e, shape: info.shape as CalendarEvent["shape"], counterparty: info.counterparty }
          : e;
      });

    return [...prospoEvents, ...withShape];
  }, [prospoMeetings, externalEvents, zoomEvents, shapes, autoImported]);

  // ── Auto-import 1-1s with known clients ────────────────────────────────────
  // Only ever sends events not already attempted. Tracking individual ids rather
  // than a signature of the whole set matters twice over: a successful import
  // removes an event (changing any set-wide key and re-firing the effect), and
  // navigating the calendar adds events that genuinely do need a pass.
  const pendingAutoImport = useMemo(
    () =>
      events
        .filter((e) => !e.inProspo && e.raw)
        .map((e) => e.raw)
        .filter((raw: any) => !attemptedRef.current.has(String(raw.externalId))),
    [events],
  );
  const autoImportKey = pendingAutoImport.map((r: any) => r.externalId).sort().join("|");

  useEffect(() => {
    if (!enabled || pendingAutoImport.length === 0) return;
    pendingAutoImport.forEach((raw: any) => attemptedRef.current.add(String(raw.externalId)));

    let cancelled = false;
    (async () => {
      try {
        const res: any = await autoImportCalendarMeetings({
          events: pendingAutoImport,
          timezone: BROWSER_TZ,
        });
        if (cancelled) return;

        const map: Record<string, { shape: string; counterparty?: any }> = {};
        for (const c of res?.classifications || []) {
          map[String(c.externalId)] = { shape: c.shape, counterparty: c.counterparty };
        }
        setShapes((prev) => ({ ...prev, ...map }));

        if (res?.imported?.length > 0) {
          setAutoImported((prev) => {
            const next = new Set(prev);
            for (const i of res.imported) next.add(String(i.externalId));
            return next;
          });
          onAutoImportedRef.current?.(res.imported);
        }
      } catch {
        // Non-fatal: the calendar still renders and "+ Add" still works. Nothing
        // is auto-filed, which is the safe direction to fail in.
      }
    })();

    return () => {
      cancelled = true;
    };
    // pendingAutoImport is intentionally not a dep — autoImportKey is its stable
    // identity, and depending on the array itself would re-fire on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, autoImportKey]);

  // `refresh` re-classifies; callers invoke it after creating a client or
  // importing a meeting. Wrapped rather than passing `load` directly so a caller
  // used as an event handler cannot pass something unintended as options.
  const refresh = useCallback(() => load({ reclassify: true }), [load]);

  return { events, loading, failed, refresh };
}
